import { useQuery } from '@apollo/client';
import { Breadcrumbs } from 'components/Breadcrumbs/Breadcrumbs';
import { Button } from 'components/Button/Button';
import { DocumentsTab } from 'components/DocumentsTabs/DocumentsTabs';
import { MemberHeader } from 'components/MemberHeader/MemberHeader';
import { MemberTabs } from 'components/MemberTabs/MemberTabs';
import { PageTitle } from 'components/PageTitle/PageTitle';
import { Pagination } from 'components/Pagination/Pagination';
import { SearchFilter } from 'components/SearchFilter/SearchFilter';
import { SortFilter } from 'components/SortFilter/SortFilter';
import { Status } from 'components/Status/Status';
import { StatusFilter } from 'components/StatusFilter/StatusFilter';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/Table/Table';
import dayjs from 'dayjs';
import { normalizeArray } from 'lib/utils';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { USERS_INVOICES } from 'services/users/query';
import { useUserStore } from 'stores/user.store';

import { MemberInvoicesActions } from './_components/MemberInvoicesActions';

const PAGE_SIZE = parseInt(localStorage.getItem('pageSize') as any) || 10;
const SORT = [
    { value: 'A_Z', label: 'A-Z' },
    { value: 'Z_A', label: 'Z-A' },
    { value: 'MOST_RECENT', label: 'Most recent' },
    { value: 'LEAST_RECENT', label: 'Least recent' },
];
const STATUSES = ['UNPAID', 'PAID', 'OVERDUE'];

export const MemberInvoices = () => {
    const user = useUserStore(state => state.user);
    const { t } = useTranslation('documents');
    const { t: b } = useTranslation('translation');
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const { data, refetch } = useQuery(USERS_INVOICES, {
        variables: { member: user?.id, first: PAGE_SIZE },
    });
    useEffect(() => {
        const search = searchParams.get('search');
        const sort = searchParams.get('sort');
        const status = searchParams.get('status')?.split(',');
        const page = searchParams.get('page') || '1';

        refetch({
            search: search,
            sort: sort,
            status: status,
            offset: (parseInt(page) - 1) * PAGE_SIZE,
        });
    }, [searchParams, refetch]);
    const location = useLocation();
    const isDocument = location.pathname.includes('/documents');

    if (!user) return null;

    return (
        <div>
            {isDocument ? (
                <>
                    <Breadcrumbs
                        crumbs={[
                            b('breadcrumbs.dashboard'),
                            b('breadcrumbs.invoices'),
                            `${user.lastName}, ${user.firstName} ${b('breadcrumbs.invoices')}`,
                        ]}
                    />
                    <DocumentsTab />
                    <PageTitle withBack>
                        {user.lastName}, {user.firstName} invoices
                    </PageTitle>
                </>
            ) : (
                <>
                    <Breadcrumbs
                        crumbs={[
                            b('breadcrumbs.dashboard'),
                            b('breadcrumbs.members'),
                            `${user.firstName} ${user.lastName} ${b('breadcrumbs.invoices')}`,
                        ]}
                    />
                    <MemberHeader />
                    <MemberTabs />
                </>
            )}

            <div className="flex justify-between items-center mb-4">
                <div className="flex gap-4 flex-wrap">
                    <SearchFilter placeholder={t('searchInvoicesPlaceholder')} />
                    <StatusFilter options={STATUSES} />
                    <SortFilter options={SORT} />
                </div>
                <Button onClick={() => navigate(`/invoices/new?memberId=${user.id}`)}>
                    + Create Invoice
                </Button>
            </div>

            <Table className="border">
                <TableHeader>
                    <TableRow>
                        <TableHead>{t('dateLabel')}</TableHead>
                        <TableHead>{t('invoiceNumberLabel')}</TableHead>
                        <TableHead>{t('typeLabel')}</TableHead>
                        <TableHead>{t('emailReminderLabel')}</TableHead>
                        <TableHead>{t('dueDateLabel')}</TableHead>
                        <TableHead>{t('invoiceStatusLabel')}</TableHead>
                        <TableHead />
                    </TableRow>
                </TableHeader>
                <TableBody className="hover:cursor-pointer">
                    {data?.invoices?.edges?.length > 0 &&
                        normalizeArray(data.invoices).map((invoice: any) => (
                            <TableRow key={invoice.id} className="text-zinc-700 hover:bg-rose-50">
                                <TableCell>{dayjs(invoice.createdAt).format('DD.MM.YYYY')}</TableCell>
                                <TableCell>{invoice.invoiceId}</TableCell>
                                <TableCell>{t(invoice.type)}</TableCell>
                                <TableCell className="hidden md:table-cell">
                                    {invoice.lastReminder ? dayjs(invoice.lastReminder).format('DD.MM.YYYY') : '-'}
                                </TableCell>
                                <TableCell className="hidden md:table-cell">
                                    {dayjs(invoice.dueDate).format('DD.MM.YYYY')}
                                </TableCell>
                                <TableCell className="hidden md:table-cell">
                                    <Status status={invoice.status} />
                                </TableCell>
                                <TableCell className="py-3 justify-end flex">
                                    <MemberInvoicesActions invoice={invoice} onUpdate={() => refetch()} />
                                </TableCell>
                            </TableRow>
                        ))}
                </TableBody>
            </Table>
            {data?.invoices?.totalCount === 0 && (
                <div className={'py-5 text-center text-zinc-700'}>{t('noResults')}</div>
            )}
            <Pagination pageInfo={data?.invoices?.pageInfo} total={data?.invoices?.totalCount} />
        </div>
    );
};
